import React, { useState, useEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged, getIdToken } from 'firebase/auth';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import FinopsUploadCard from './FinopsUploadCard';
import FinopsMetricsSummary from './FinopsMetricsSummary';
import RecommendationsTable from './RecommendationsTable';
import RemediationConsole from './RemediationConsole';
import SavingsBarChart from './SavingsBarChart';
import ClusterTelemetrySection from './ClusterTelemetrySection';
import FinopsPricingModal from '../../components/FinopsPricingModal';
import GreenOpsPortal from './GreenOpsPortal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-core-644060845776.us-central1.run.app';

const FinopsDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    i18n.changeLanguage('en');
  }, [i18n]);

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [registeredClusters, setRegisteredClusters] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedClusterId, setSelectedClusterId] = useState('');
  const [selectedRec, setSelectedRec] = useState(null);
  const [costAnomalyDetected, setCostAnomalyDetected] = useState(false);
  const [estimatedSavingsUsd, setEstimatedSavingsUsd] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [userTier, setUserTier] = useState('developer');
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'portal'

  const getFirebaseToken = async () => {
    if (auth.currentUser) {
      return await getIdToken(auth.currentUser, true);
    }
    return '';
  };

  const fetchRegisteredClusters = useCallback(async (currentUser) => {
    const activeUser = currentUser || auth.currentUser;
    if (!activeUser) return;

    try {
      const token = await getIdToken(activeUser);
      const url = `${BACKEND_URL}/api/finops/clusters`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setRegisteredClusters(response.data);
        if (response.data.length > 0 && !selectedClusterId) {
          setSelectedClusterId(response.data[0].id);
        }
      }
    } catch (err) {
      console.error("Error fetching registered clusters:", err);
    }
  }, [auth, selectedClusterId]);

  const fetchRecommendations = useCallback(async (currentUser) => {
    const activeUser = currentUser || auth.currentUser;
    if (!activeUser) return;

    try {
      const token = await getIdToken(activeUser);
      let url = `${BACKEND_URL}/api/finops/recommendations`;
      if (selectedClusterId) {
        url += `?cluster_id=${encodeURIComponent(selectedClusterId)}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setRecommendations(response.data);
        if (response.data.length > 0) {
          const latest = response.data[0];
          setCostAnomalyDetected(latest.cost_anomaly_detected || false);
          setEstimatedSavingsUsd(latest.estimated_savings_usd || 0);
          
          // Preserve previous selection if it still exists, otherwise choose the first one
          setSelectedRec(prev => {
            if (prev) {
              const stillExists = response.data.find(r => r.id === prev.id);
              return stillExists || response.data[0];
            }
            return response.data[0];
          });
        } else {
          setCostAnomalyDetected(false);
          setEstimatedSavingsUsd(0);
          setSelectedRec(null);
        }
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setErrorMsg(err.response?.data?.error || err.message);
    } finally {
      setLoadingData(false);
    }
  }, [auth, selectedClusterId]);

  useEffect(() => {
    if (user) {
      fetchRecommendations();
    }
  }, [selectedClusterId, user, fetchRecommendations]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          setUserTier(idTokenResult.claims?.tier || 'developer');
          setUser(currentUser);
          // Initial fetches: fetchRegisteredClusters will resolve clusters and set selectedClusterId
          fetchRegisteredClusters(currentUser);
        } catch (err) {
          console.error("Error verifying claims", err);
          navigate('/');
        }
      } else {
        setUser(null);
        setLoadingData(false);
      }
      setLoadingUser(false);
    });
    return () => unsubscribe();
  }, [auth, navigate, fetchRegisteredClusters]);

  const handleUploadSuccess = (jobId) => {
    setIsPolling(true);
    setLoadingData(true);
    let attempts = 0;
    const maxAttempts = 15; // Poll up to 60 seconds (15 * 4s)
    
    const interval = setInterval(async () => {
      attempts++;
      try {
        if (jobId) {
          const token = await getFirebaseToken();
          const jobRes = await axios.get(`${BACKEND_URL}/api/finops/audit/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (jobRes.data && (jobRes.data.status === 'COMPLETED' || jobRes.data.status === 'FAILED')) {
            clearInterval(interval);
            setIsPolling(false);
            if (jobRes.data.status === 'FAILED' && jobRes.data.error_message) {
              setErrorMsg(`Audit error: ${jobRes.data.error_message}`);
              setTimeout(() => setErrorMsg(null), 6000);
            }
            await Promise.all([fetchRecommendations(), fetchRegisteredClusters()]);
            return;
          }
        }
      } catch (err) {
        console.warn("Job status polling notice:", err);
      }

      await Promise.all([fetchRecommendations(), fetchRegisteredClusters()]);
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setIsPolling(false);
      }
    }, 4000);
  };

  const handleManualRefresh = async () => {
    setLoadingData(true);
    await Promise.all([fetchRecommendations(), fetchRegisteredClusters()]);
  };

  const getActiveClusterId = () => {
    const targetRec = selectedRec || (recommendations.length > 0 ? recommendations[0] : null);
    if (targetRec && targetRec.cluster_id && targetRec.cluster_id !== 'null' && targetRec.cluster_id !== 'undefined') {
      return String(targetRec.cluster_id).trim();
    }
    return '';
  };

  const handleDownloadReport = async () => {
    const activeClusterId = getActiveClusterId();
    if (!activeClusterId) return;
    
    if (userTier === 'developer') {
      setShowPricingModal(true);
      return;
    }
    
    setGeneratingReport(true);
    try {
      const token = await getFirebaseToken();
      const lang = 'en';
      const url = `${BACKEND_URL}/api/finops/report?cluster_id=${encodeURIComponent(activeClusterId)}&lang=${lang}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (err) {
      console.error("Error generating report:", err);
      setErrorMsg(err.response?.data?.error || "Error generating report");
      setTimeout(() => setErrorMsg(null), 6000);
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loadingUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-secondary)' }}>
        {t('app.loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: '20px', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div className="glass-panel" style={{ padding: '40px', maxWidth: '400px', width: '100%' }}>
          <div className="logo-text" style={{ marginBottom: '20px' }}>On<span className="logo-accent">Track</span> GreenOps</div>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>{t('finops.login_required')}</p>
          <Link to="/?redirect=/finops" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-block' }}>{t('finops.go_to_login')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', paddingTop: 'calc(40px + env(safe-area-inset-top, 0px))', fontFamily: "'Inter', system-ui, sans-serif", maxWidth: '1200px', margin: '0 auto', minHeight: '100vh' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <div className="logo-text" style={{ fontSize: '1.8rem' }}>
          On<span className="logo-accent">Track</span> <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)', fontWeight: 'normal' }}>GreenOps</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.9rem', marginRight: '10px' }}>
            ← {t('finops.back')}
          </Link>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{user.displayName?.split(' ')[0]}</p>
          <button onClick={() => { auth.signOut(); navigate('/'); }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', cursor: 'pointer', padding: '5px 10px', borderRadius: '6px', fontSize: '0.8rem' }}>
            {t('app.logout')}
          </button>
        </div>
      </div>

      {/* Plan Pill */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button onClick={() => userTier === 'developer' && setShowPricingModal(true)} style={{
          background: userTier === 'developer' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
          border: `1px solid ${userTier === 'developer' ? '#EF4444' : '#10B981'}`,
          color: userTier === 'developer' ? '#FCA5A5' : '#A7F3D0',
          padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold',
          cursor: userTier === 'developer' ? 'pointer' : 'default', transition: 'all 0.2s'
        }}>
          {t('finops.plan_label')}: {userTier.toUpperCase()} {userTier === 'developer' && ` (${t('finops.click_to_upgrade')})`}
        </button>
      </div>

      {/* Main Title Banner */}
      <div style={{ marginBottom: '40px', textAlign: 'left' }}>
        <h1 className="gradient-text" style={{ fontSize: '2.5rem', margin: '0 0 10px 0' }}>
          {t('finops.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', margin: '0', maxWidth: '800px', lineHeight: '1.5' }}>
          {t('finops.subtitle')}
        </p>
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '15px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '30px', paddingBottom: '10px' }}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'overview' ? '2px solid var(--primary-color)' : 'none',
            color: activeTab === 'overview' ? '#FFF' : 'var(--text-secondary)',
            fontSize: '1rem',
            fontWeight: 'bold',
            padding: '8px 16px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          📈 {t('finops.portal.tab_overview')}
        </button>
        <button
          onClick={() => setActiveTab('portal')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'portal' ? '2px solid var(--primary-color)' : 'none',
            color: activeTab === 'portal' ? '#FFF' : 'var(--text-secondary)',
            fontSize: '1rem',
            fontWeight: 'bold',
            padding: '8px 16px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          ☸️ {t('finops.portal.tab_clusters')}
        </button>
      </div>

      {activeTab === 'overview' ? (
        <>
          {/* Upload Zone */}
          <FinopsUploadCard
            clusterId={selectedClusterId}
            getFirebaseToken={getFirebaseToken}
            onUploadSuccess={handleUploadSuccess}
            onError={(err) => {
              setErrorMsg(err);
              setTimeout(() => setErrorMsg(null), 6000);
            }}
          />

          {/* Real-time Cluster Telemetry & Efficiency Section */}
          <ClusterTelemetrySection
            selectedClusterId={selectedClusterId || (registeredClusters.length > 0 ? registeredClusters[0].id : 'default-cluster')}
            onSelectCluster={(cid) => {
              setSelectedClusterId(cid);
            }}
            clusters={registeredClusters}
            getFirebaseToken={getFirebaseToken}
            backendUrl={BACKEND_URL}
            userTier={userTier}
            onTriggerPricing={() => setShowPricingModal(true)}
          />

          {loadingData && recommendations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
              <span className="spinner" style={{
                display: 'inline-block',
                width: '30px',
                height: '30px',
                border: '3px solid rgba(79, 70, 229, 0.3)',
                borderTopColor: 'var(--primary-color)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '10px'
              }}></span>
              <p>{t('finops.loading_recommendations')}</p>
            </div>
          ) : (
            <>
              {/* Metrics summary cards */}
              <FinopsMetricsSummary
                costAnomalyDetected={costAnomalyDetected}
                estimatedSavingsUsd={estimatedSavingsUsd}
                recommendations={recommendations}
              />

              {isPolling && (
                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  color: '#93C5FD',
                  borderRadius: '10px',
                  padding: '12px 18px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}>
                  <span className="pulse-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#60A5FA', display: 'inline-block', animation: 'pulse 1.5s infinite' }}></span>
                  {t('finops.background_analyzing')}
                </div>
              )}

              {/* Refresh and title list */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>{t('finops.active_recommendations')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {getActiveClusterId() && (
                    <button
                      onClick={handleDownloadReport}
                      disabled={generatingReport}
                      style={{
                        background: 'var(--primary-color, #006666)',
                        border: 'none',
                        color: '#fff',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: generatingReport ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: generatingReport ? 0.6 : 1,
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      {generatingReport ? (
                        <>
                          <span className="spinner" style={{
                            display: 'inline-block',
                            width: '12px',
                            height: '12px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                          {t('finops.generating_report')}
                        </>
                      ) : (
                        <>
                          📄 {t('finops.download_report')}
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={handleManualRefresh}
                    disabled={loadingData}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary-color)',
                      fontSize: '1.2em',
                      cursor: 'pointer',
                      padding: '0 5px',
                      transition: 'transform 0.5s',
                      transform: loadingData ? 'rotate(360deg)' : 'none'
                    }}
                    title={t('finops.refresh')}
                  >
                    🔄
                  </button>
                </div>
              </div>

              {/* Grid Layout: Tables, Console, Charts / Empty State */}
              {recommendations.length === 0 ? (
                <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '30px' }}>
                  <p style={{ fontSize: '1.1rem', margin: 0 }}>✨ {t('finops.reports.pdf_no_recommendations')}</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '30px', minWidth: 0 }} className="finops-grid min-w-0">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }} className="min-w-0">
                    <RecommendationsTable
                      recommendations={recommendations}
                      selectedRec={selectedRec}
                      onSelectRec={setSelectedRec}
                    />
                    <RemediationConsole recommendation={selectedRec} />
                  </div>
                  <div style={{ minWidth: 0 }} className="min-w-0">
                    <SavingsBarChart recommendations={recommendations} />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <GreenOpsPortal
          userTier={userTier}
          getFirebaseToken={getFirebaseToken}
          backendUrl={BACKEND_URL}
          onTriggerPricing={() => setShowPricingModal(true)}
        />
      )}

      {errorMsg && (
        <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: '#EF4444', color: 'white', padding: '12px 24px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)', zIndex: 100 }}>
          {errorMsg}
        </div>
      )}

      {showPricingModal && (
        <FinopsPricingModal user={user} onClose={() => setShowPricingModal(false)} />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { opacity: 0.4; }
          50% { opacity: 1; }
          100% { opacity: 0.4; }
        }
        @media (min-width: 1024px) {
          .finops-grid {
            grid-template-columns: 1.3fr 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FinopsDashboard;
